import { Chip, HSpacer, Text } from '@/components/DesignSystem';
import { Filter, FilterSelection } from '@/components/DesignSystem/Toolbar/interfaces';
import { SXStyles } from '@/themes/variant-interfaces/SXStyles';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Stack, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import Sort from "@mui/icons-material/Sort";
import { truncate } from 'lodash';

const styles: SXStyles = {
  dropdownIcon: {
    height: '18px',
    marginRight: '-4px',
    width: '18px',
  },
} as const;

interface FilterChipProps {
  disabled?: boolean,
  filter: Filter,
  onClick?: React.MouseEventHandler<HTMLDivElement>,
  selection?: FilterSelection,
  testID: string,
}

export const FilterChip = ({
  disabled = false,
  filter,
  onClick,
  selection,
  testID,
}: FilterChipProps) => {
  const theme = useTheme();
  const isBoolean = filter.selectionMethod === 'boolean';
  const showCount = ['chips', 'multi-select'].includes(filter.selectionMethod);
  const selectionCount = selection?.size ?? 0;
  const hasSubOptions = filter.options.some((option) => !!option.subOptions?.length);
  const allOptions = useMemo(() => (
    filter.options.flatMap((option) => (
      [option, ...(option.subOptions ?? [])]
    ))
  ), [filter.options]);

  const getLabelFromId = (id: string) => {
    const label = allOptions.find((option) => option.id === id)?.label ?? '';
    const { truncateLabel = true } = filter;
    if (truncateLabel) {
      return truncate(label, { length: 20 });
    }
    return label;
  };
  let labelText = filter.label;
  if (filter.isChipLabelVerbose && selection?.size) {
    const firstValue = selection.values().next().value;
    labelText = getLabelFromId(firstValue ?? '');
    if (filter.selectionMethod === 'multi-select' && selectionCount > 1) {
      labelText += ` +${selectionCount - 1} more`;
    }
  } else if (selectionCount && showCount && !hasSubOptions) {
    labelText = `${selectionCount} ${filter.label}`;
  }

  const showSelectedState = !!selection?.size;

  return (
    <Chip
      disabled={disabled}
      key={filter.id}
      label={
        <Stack alignItems="center" direction="row">
          {(showSelectedState && !isBoolean) && (
            <>
              <Check sx={{ width: "18px" }} />
              <HSpacer size="3" />
            </>
          )}
          {!!filter.startIcon && (
            <Stack direction="row">
              {filter.startIcon === 'sort' && (
                <Sort sx={{ width: "18px", height: "18px" }}/>
              )}
              <HSpacer size="3" />
            </Stack>
          )}
          <Text category="label-large" testID={`${testID}-label`}>
            {labelText}
          </Text>
          {(showSelectedState && isBoolean) && (
            <>
              <HSpacer size="3" />
              <Close sx={{ width: "18px" }} />
            </>
          )}
          {!isBoolean && (
            <>
              <HSpacer size="3"/>
              <ArrowDropDown sx={styles.dropdownIcon} />
            </>
          )}
        </Stack>
      }
      {...(disabled ? {} : { onClick })}
      size="medium"
      sx={{
        "&:hover": {
          backgroundColor: showSelectedState ? theme.palette.primaryContainer : undefined,
        },
        borderColor: "#bdbdbd",
        backgroundColor: showSelectedState ? theme.palette.primaryContainer : undefined,
      }}
      testID={testID}
      variant={showSelectedState ? 'filled' : 'outlined'}
    />
  );
};
